import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Container, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { TableSearch } from "../../components/form/Fields";
import CustomTable from "../../components/table/CustomTable";
import StatusChip from "../../components/statusChip/StatusChip";
import { useToast } from "../../context/TostContext";
import { usePermission } from "../../hooks/usePermission";
import { formatMomentDate } from "../../utils/dateHelper";
import FeedbackDetail from "./FeedbackDetail";
import { listFeedback } from "./feedbackApi";
import { CATEGORY_LABELS, MODULE_LABELS, PRODUCTS, STATUSES, statusMeta } from "./constants";

const STATUS_FILTERS = [{ value: "", label: "All" }, ...STATUSES];

/**
 * One screen, used twice: R-Point Feedback and MyEstatePoint Feedback.
 *
 * Feedback for both products is stored together, but each screen only ever
 * asks for its own product, and the backend checks feedback_<product> on every
 * call — so the separation holds even if someone edits the URL.
 */
const Feedback = ({ product }) => {
    const config = PRODUCTS[product];
    const toast = useToast();
    const { hasPermission } = usePermission();
    const canUpdate = hasPermission(config.permission, "update");

    const { user } = useSelector((state) => state.auth);
    const currentAdminId = (user?.data?.admin || user?.data)?.id || null;

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("new");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await listFeedback(product, {
                page: currentPage,
                limit: pageSize,
                status: statusFilter || undefined,
                category: categoryFilter || undefined,
                search: debouncedSearch || undefined,
            });
            setRows(result.data || []);
            setTotal(result.meta?.total || 0);
        } catch (err) {
            setRows([]);
            setTotal(0);
            setError(
                err.status === 403
                    ? "You don't have permission to view this feedback. Ask an admin to grant it in Roles."
                    : err.message
            );
        } finally {
            setLoading(false);
        }
    }, [product, currentPage, pageSize, statusFilter, categoryFilter, debouncedSearch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const resetTo = (setter) => (value) => {
        setter(value);
        setCurrentPage(1);
    };

    const columns = useMemo(
        () => [
            {
                id: "received",
                header: "RECEIVED",
                cell: (_, row) => (
                    <span className="text-nowrap">
                        {formatMomentDate(row.created_at, "DD MMM YYYY")}
                        <small className="d-block text-muted">
                            {formatMomentDate(row.created_at, "hh:mm A")}
                        </small>
                    </span>
                ),
            },
            {
                id: "category",
                header: "PROBLEM",
                className: "text-start",
                cell: (_, row) => (
                    <div className="d-flex flex-column" style={{ maxWidth: 360 }}>
                        <span className="fw-medium">{CATEGORY_LABELS[row.category] || row.category}</span>
                        <small className="text-muted text-truncate">{row.message}</small>
                    </div>
                ),
            },
            {
                id: "about",
                header: "ABOUT",
                className: "text-start",
                cell: (_, row) => (
                    <div className="d-flex flex-column">
                        <span>{MODULE_LABELS[row.module] || row.module}</span>
                        {row.context?.sheet_title && (
                            <small className="text-muted text-truncate" style={{ maxWidth: 180 }}>
                                {row.context.sheet_title}
                            </small>
                        )}
                    </div>
                ),
            },
            {
                id: "sender",
                header: "SENT BY",
                className: "text-start",
                cell: (_, row) => (
                    <div className="d-flex flex-column">
                        <span className="fw-medium">{row.user_name || "-"}</span>
                        <small className="text-muted">{row.user_email || row.user_phone || "-"}</small>
                    </div>
                ),
            },
            {
                id: "files",
                header: "FILES",
                cell: (_, row) => (row.attachment_count ? row.attachment_count : "-"),
            },
            {
                id: "assignee",
                header: "ASSIGNED",
                cell: (_, row) => row.assignee?.name || "-",
            },
            {
                id: "status",
                header: "STATUS",
                cell: (_, row) => {
                    const s = statusMeta(row.status);
                    return <StatusChip bg={s.bg} pill title={s.label} />;
                },
            },
        ],
        []
    );

    const pagination = useMemo(
        () => ({
            total,
            totalPages: Math.ceil(total / pageSize),
            currentPage,
            setCurrentPage,
            pageSize,
            setPageSize: (size) => {
                setPageSize(size);
                setCurrentPage(1);
            },
            onPageChange: (page) => setCurrentPage(page),
        }),
        [total, currentPage, pageSize]
    );

    const headerPortalTarget = document.getElementById("header-portal-target");
    const headerContent = (
        <div className="d-flex align-items-center justify-content-between w-100">
            <h4 className="fw-semibold mb-0 text-primary fs-lg">
                <span className="text-muted fw-normal">Feedback / </span>
                {config.title}
            </h4>
        </div>
    );

    return (
        <>
            {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
            <Container fluid className="px-0">
                <div className="bg-white rounded-2 overflow-hidden common-shadow mt-3">
                    <div className="p-4 d-flex align-items-center justify-content-between gap-3 flex-wrap">
                        <TableSearch
                            value={searchTerm}
                            placeholder="Search message, name, email or mobile"
                            onChange={(e) => resetTo(setSearchTerm)(e.target.value)}
                            onClear={() => resetTo(setSearchTerm)("")}
                        />
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Form.Select
                                size="sm"
                                style={{ width: 230 }}
                                value={categoryFilter}
                                onChange={(e) => resetTo(setCategoryFilter)(e.target.value)}
                                aria-label="Filter by problem"
                            >
                                <option value="">All problems</option>
                                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </Form.Select>
                            {STATUS_FILTERS.map((filter) => (
                                <button
                                    key={filter.value || "all"}
                                    type="button"
                                    className={`btn btn-sm ${
                                        statusFilter === filter.value ? "btn-primary" : "btn-outline-secondary"
                                    }`}
                                    onClick={() => resetTo(setStatusFilter)(filter.value)}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {error && <div className="alert alert-danger mx-4">{error}</div>}

                    <CustomTable
                        data={rows}
                        columns={columns}
                        loading={loading}
                        emptyMessage={
                            statusFilter === "new" && !debouncedSearch && !categoryFilter
                                ? "No new feedback. You're all caught up."
                                : "No feedback matches these filters."
                        }
                        keyField="id"
                        pagination={pagination}
                        rowClick={(row) => setOpenId(row.id)}
                    />
                </div>
            </Container>

            <FeedbackDetail
                product={product}
                feedbackId={openId}
                canUpdate={canUpdate}
                currentAdminId={currentAdminId}
                toast={toast}
                onClose={() => setOpenId(null)}
                onChanged={(updated) =>
                    setRows((current) =>
                        current.map((r) =>
                            r.id === updated.id
                                ? { ...r, status: updated.status, assignee: updated.assignee }
                                : r
                        )
                    )
                }
            />
        </>
    );
};

export default Feedback;
